import React from "react";
import { motion } from "framer-motion";
import { User, Target, Lightbulb, Award } from "lucide-react";
import { personalInfo, skills } from "../data/mock";

const About = () => {
  const highlights = [
    {
      icon: Target,
      title: "Mission",
      description:
        "To create innovative, scalable solutions that make a real impact on users' lives.",
    },
    {
      icon: Lightbulb,
      title: "Vision",
      description:
        "Pushing the boundaries of web development with cutting-edge microfrontend architectures.",
    },
    {
      icon: Award,
      title: "Values",
      description:
        "Quality, innovation, and continuous learning drive everything I do.",
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About{" "}
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              Me
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Get to know more about my journey, skills, and what drives me as a
            developer
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="bg-[rgb(15,20,30)] rounded-2xl p-8 border border-white/5 sticky top-32">
              <div className="w-32 h-32 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <User size={64} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                {personalInfo.name}
              </h2>
              <p className="text-gray-400 text-center mb-6">
                {personalInfo.tagline}
              </p>
            </div>
          </motion.div>

          {/* Story & Highlights */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2 space-y-8"
          >
            {/* Story */}
            <div className="bg-[rgb(15,20,30)] rounded-2xl p-8 border border-white/5">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <User className="text-blue-400" size={20} />
                </div>
                My Story
              </h3>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  I'm a passionate Full Stack Developer with a strong focus on
                  creating scalable, maintainable web applications. My journey
                  in software development started with a curiosity about how
                  things work on the web, and it has evolved into a career
                  dedicated to building exceptional digital experiences.
                </p>
                <p>
                  With expertise in modern JavaScript frameworks, particularly
                  React and TypeScript, I specialize in microfrontend
                  architectures that enable teams to build and deploy features
                  independently. I believe in writing clean, efficient code that
                  not only works but is also a pleasure to maintain.
                </p>
                <p>
                  When I'm not coding, you'll find me exploring new
                  technologies, contributing to open-source projects, or sharing
                  my knowledge through technical blog posts and mentoring junior
                  developers. I'm always excited to tackle challenging problems
                  and learn something new.
                </p>
              </div>
            </div>

            {/* Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              {highlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-[rgb(15,20,30)] rounded-xl p-6 border border-white/5 hover:border-blue-500/30 transition-all"
                >
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="text-blue-400" size={24} />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {item.title}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Skills Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-[rgb(15,20,30)] rounded-2xl p-8 border border-white/5"
        >
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            Technical Skills
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(skills).map(([category, items], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <h4 className="text-lg font-semibold text-white mb-4 capitalize flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  {category}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {items.map((skill, i) => (
                    <motion.span
                      key={i}
                      whileHover={{ scale: 1.05 }}
                      className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium border border-blue-500/20 hover:bg-blue-500/20 transition-all cursor-default"
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
